/**
 * BLU-3xx Phase 47.01.2-B-fix3 — useTargetCatalog
 *
 * Shared hook for the cross-org write modals (AddStockModal,
 * IndividualStocktakeModal, UnavailableAdjustmentModal). Resolves the SKU
 * options for the modal's product dropdown:
 *
 *  - When `targetOrgId` is set AND ≠ caller's own org: fetches the target
 *    org's catalog via `inventory.listSkus({ targetOrganizationId,
 *    locationId })`. The backend gates this via the same authorization
 *    helper (`gateTargetOrganization`) that addStock uses + tightens RBAC
 *    to write-side roles only + strips cost columns. The catalog rows
 *    are mapped into the `Product` shape used by all three modals.
 *
 *  - Otherwise (own-org receive, single-tenant, or modal closed): returns
 *    the caller-supplied `ownProducts` prop unchanged. This preserves
 *    every existing single-tenant code path byte-for-byte.
 *
 * Without this hook each modal would triplicate ~80 lines of conditional
 * query + mapping logic — three places to forget to update when the API
 * surface changes. Centralizing here makes the cross-org dropdown a single
 * named primitive.
 *
 * @see apps/backend/src/modules/inventory/inventory.handler.ts (listSkus)
 */

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import type { Product } from '../types';
import { filterToTargetOrg } from '../utils/filterToTargetOrg';

interface UseTargetCatalogArgs {
  /** The user's own org id. Used to detect "own-org target" short-circuit. */
  ownOrgId: string | undefined;
  /** Currently chosen target org from ClientTargetPicker, or null when unset. */
  targetOrgId: string | null;
  /** Effective location id for the modal (selected location or fallback). */
  locationId: string | undefined;
  /**
   * Caller's own pre-fetched product list (from `getProducts` via
   * `useInventory` or the modal's location-fallback query). Used in
   * single-tenant / own-org / no-target scenarios.
   */
  ownProducts: Product[];
  /** Whether the modal is open (no point fetching when closed). */
  isOpen: boolean;
}

interface UseTargetCatalogResult {
  /** Mapped to `Product[]` whether sourced from `ownProducts` or `listSkus`. */
  options: Product[];
  /** True only while the cross-org `listSkus` query is in flight. */
  isLoading: boolean;
  /**
   * BLU-669: target-agnostic — true when a target org is picked (own OR
   * client) and its resolved option list is empty. The pre-BLU-669 shape was
   * hard-wired to the cross-org branch (`wantsCrossOrg && …`), which made the
   * empty state unreachable for an own-org target — filtering the own-org
   * list (see below) without this would have shipped a silent blank dropdown.
   */
  isEmpty: boolean;
  /**
   * True when the cross-org branch is active. Modals use this to decide
   * whether to render the "{client}'s catalog" empty-state copy vs. the
   * own-org variant.
   */
  isCrossOrg: boolean;
}

export const useTargetCatalog = ({
  ownOrgId,
  targetOrgId,
  locationId,
  ownProducts,
  isOpen,
}: UseTargetCatalogArgs): UseTargetCatalogResult => {
  // Active only when the operator has explicitly picked another org.
  // Own-org target (target === ownOrgId) short-circuits — no need to
  // fetch the catalog twice; `ownProducts` already covers it.
  const wantsCrossOrg =
    !!targetOrgId && !!ownOrgId && targetOrgId !== ownOrgId && !!locationId;

  // `wantsCrossOrg` ensures targetOrgId and locationId are non-null/undefined,
  // but TS narrowing through a const can't fully prove that. Use `??` fallbacks
  // — when `wantsCrossOrg` is false the query is `enabled: false` and the
  // fallback values are never sent.
  const crossOrgQuery = trpc.inventory.listSkus.useQuery(
    wantsCrossOrg
      ? {
          targetOrganizationId: targetOrgId ?? '',
          locationId: locationId ?? '',
        }
      : {},
    {
      enabled: isOpen && wantsCrossOrg,
      staleTime: 30_000,
    },
  );

  const options = useMemo<Product[]>(() => {
    // BLU-669: the own-org branch must NOT return `ownProducts` unfiltered.
    // At a shared 3PL location `getProducts` legitimately includes connected
    // clients' rows; offering those under an own-org target sent the write to
    // resolveRowOrgForWrite's "belongs to a different organization" rejection
    // every time (deterministically, at a location where the owner holds no
    // catalog of its own). Narrow to the picked target via the shared filter.
    if (!wantsCrossOrg) return filterToTargetOrg(ownProducts, targetOrgId, ownOrgId);
    // CR-HIGH-1 (defensive): zero options while isFetching so a target
    // switch (Org A → Org B) can never momentarily expose Org A's catalog
    // through `options`. The skeleton render path already gates the
    // visible UI on `isLoading`, but stripping options at the source
    // protects future consumers that may read `options` without
    // checking `isLoading`.
    if (crossOrgQuery.isFetching) return [];
    const data = crossOrgQuery.data ?? [];
    return data.map(
      (row): Product => ({
        id: row.id,
        sku: row.sku,
        title: row.title ?? undefined,
        category: {
          type: row.category?.type ?? undefined,
          size: row.category?.size ?? undefined,
          color: row.category?.color ?? undefined,
        },
        // No inventory yet for cross-org targets — these are catalog rows
        // before first receipt. Stock fields default to 0 so the dropdown
        // can render "out of stock" badges if it likes.
        stockLevel: 0,
        onHand: 0,
        available: 0,
        committed: 0,
        unavailable: 0,
        incoming: 0,
        image_url: row.imageUrl ?? undefined,
        // Cost fields are intentionally absent — backend strips them on
        // cross-org calls (CRIT-1). Surface as undefined for type safety.
        skuCostOverride: null,
        effectiveCost: undefined,
        isLocked: row.isLocked ?? false,
        // Polish (post-review): guard productionType against a future enum
        // expansion. If a third type ever lands the cast would silently
        // mislabel; explicit narrowing surfaces the drift instead.
        productionType: row.productionType === 'PAP' ? 'PAP' : 'POD',
        // Stamp the owner so any badge logic in the dropdown can render
        // a client-org pill. This is THE catalog of the picked client.
        ownerOrganizationId: targetOrgId ?? undefined,
      }),
    );
  }, [
    wantsCrossOrg,
    crossOrgQuery.data,
    crossOrgQuery.isFetching,
    ownProducts,
    targetOrgId,
    ownOrgId,
  ]);

  const isLoading = wantsCrossOrg && crossOrgQuery.isFetching;

  return {
    options,
    isLoading,
    // BLU-669: target-agnostic. Fires for an own-org target too (the owner
    // holding zero catalog at a shared location is the common 3PL shape), so
    // the modal can render an explicit empty state instead of a blank list.
    isEmpty: !!targetOrgId && !isLoading && options.length === 0,
    isCrossOrg: wantsCrossOrg,
  };
};
